import { BillingControlPage } from "./BillingControlPage";
import { BillingDiagnosticsPage } from "./BillingDiagnosticsPage";
import { RefundDialog } from "./RefundDialog";
import { createOperationId } from "../operationId";
import { ReferencePricesPage } from "./ReferencePricesPage";
import { useEffect, useState } from "react";
import { NodeCostsPage } from "./NodeCostsPage";
import { SandboxUsagePage } from "./SandboxUsagePage";
import { NavLink, Navigate, Route, Routes, Link, useSearchParams } from "react-router-dom";
import { listIDSTeams, requestJSON, type IDSTeam } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementEmpty,
  ManagementHeader,
  ManagementLoading,
  ManagementMessages,
  ManagementPagination,
  ManagementPanel,
} from "../managementUI";
import { LLMUsage } from "../monitoring/LLMUsage";
import "../monitoring/monitoring.css";
import "./billing.css";

const categories = [
  "token",
  "sandbox",
  "storage",
  "database",
  "publish",
] as const;
const names = {
  token: ["Token", "Tokens"],
  sandbox: ["沙箱", "Sandboxes"],
  storage: ["存储", "Storage"],
  database: ["数据库", "Databases"],
  publish: ["发布环境", "Published environments"],
} as const;
type Rule = {
  mode: string;
  factor: string;
  unit_price_usd: string;
  unit_prices_usd?: Record<string, string>;
  unit: string;
  permitted_basis: string[];
};
export type Rules = {
  revision: number;
  points_per_usd: string;
  categories: Record<string, Rule>;
  usage_control?: {
    model: { enabled: boolean; interval_seconds: number };
    sandbox: { enabled: boolean; interval_seconds: number; grace_seconds: number; mode: string };
    publish: { enabled: boolean; interval_seconds: number; grace_seconds: number; mode: string; auto_resume: boolean };
  };
  storage_insufficient_balance: {
    mode: string;
    max_debt_points: string | null;
    retention_mode: string;
    retention_days: number | null;
    automatic_enforcement: false;
  };
  publish_insufficient_balance: {
    mode: string;
    grace_seconds: number | null;
    automatic_enforcement: false;
  };
};
type Entry = {
  id: string;
  team_id: string | null;
  settlement_status?: "review" | "settled" | "pending" | "unattributed" | "legacy_review" | "unpriced" | "source_retired" | null;
  settlement_reason?: string | null;
  action: string;
  category: string | null;
  balance_delta: string;
  reference_id?: string | null;
  refunded_points?: string | null;
  refundable_points?: string | null;
  created_ms: number;
  snapshot: {
    evidence?: {
      reported_usd: string | null;
      cost_basis: string;
      measurement_basis?: string;
      source: string;
      source_id: string;
      resource_ref?: string;
      quantity?: string | null;
      unit?: string;
      usage_details?: Record<string, number | null>;
    };
    rules?: { revision: number };
  };
};
// Metering counters the ledger accepts, in the order they are shown. A counter
// the gateway did not report is absent rather than zero, so the column never
// claims a measurement that was never taken.
const usageNames: Record<string, [string, string]> = {
  input_tokens: ["输入", "In"],
  output_tokens: ["输出", "Out"],
  cache_read_tokens: ["缓存读", "Cache read"],
  cache_write_tokens: ["缓存写", "Cache write"],
  reasoning_tokens: ["推理", "Reasoning"],
  cpu_milli: ["CPU（毫核）", "CPU (millicores)"],
  memory_bytes: ["内存（字节）", "Memory (bytes)"],
  duration_ms: ["时长（毫秒）", "Duration (ms)"],
  byte_seconds: ["字节秒", "Byte-sec"],
  storage_bytes_start: ["起始容量（字节）", "Start size (bytes)"],
  storage_bytes_end: ["结束容量（字节）", "End size (bytes)"],
  source_measured_start_ms: ["起始容量测量时间（毫秒）", "Start source measurement (ms)"],
  source_measured_end_ms: ["结束容量测量时间（毫秒）", "End source measurement (ms)"],
};
const basisNames: Record<string, [string, string]> = {
  gateway_reported: ["网关报告", "Gateway reported"],
  provider_reported: ["供应商报告", "Provider reported"],
  estimated: ["明确估算", "Estimated"],
  invoice_confirmed: ["账单确认", "Confirmed invoice"],
  unknown: ["金额未知", "Amount unknown"],
};
const basisOptions = [
  ["gateway_reported", ["网关报告", "Gateway report"]],
  ["provider_reported", ["供应商报告", "Provider report"]],
  ["estimated", ["明确估算", "Explicit estimate"]],
  ["invoice_confirmed", ["账单确认", "Confirmed invoice"]],
] as const;
// What each pricing mode actually does to a Team, and how it is badged. The
// hint is the answer to "现在到底收不收费" without reading the rule fields.
const modeInfo: Record<
  string,
  { tone: string; label: [string, string]; hint: [string, string] }
> = {
  disabled: {
    tone: "is-disabled",
    label: ["未启用", "Disabled"],
    hint: [
      "不参与计价：执行端既不会预留也不会扣点，只保留费用快照。",
      "Not priced: nothing is reserved or charged; cost snapshots are still recorded.",
    ],
  },
  free: {
    tone: "is-free",
    label: ["免费", "Free"],
    hint: [
      "记录用量但点数按 0 计。适合先验证采集与预留链路，再切真实计价。",
      "Usage is recorded but priced at zero. Use it to validate the pipeline first.",
    ],
  },
  cost_factor: {
    tone: "is-metered",
    label: ["费用乘系数", "Cost multiplier"],
    hint: [
      "按报告费用乘系数计点。必须勾选允许的依据类型，估算不能冒充供应商报告金额。",
      "Charges reported cost times a multiplier. Permitted bases must be selected explicitly.",
    ],
  },
  resource_price: {
    tone: "is-metered",
    label: ["资源分项单价", "Resource unit prices"],
    hint: ["按资源采样计算 CPU、内存或容量占用的点数。单价是客户计价，不是供应商实际成本。", "Charges sampled CPU, memory or storage occupancy. These are customer rates, not supplier costs."],
  },
  unit_price: {
    tone: "is-metered",
    label: ["用量乘单价", "Unit price"],
    hint: [
      "按用量乘单价计点。计量单位必须与上报单位逐字一致，否则拒绝结算。",
      "Charges usage times a unit price. The unit must match the reported unit exactly.",
    ],
  },
};
function windowQuery(team = "", days = 30) {
  const end = Date.now();
  const q = new URLSearchParams({
    start_ms: String(end - days * 86400000),
    end_ms: String(end),
  });
  if (team) q.set("team_id", team);
  return q.toString();
}
const tabClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? "page-tab page-tab-active" : "page-tab";

export function BillingPage({ csrfToken, administratorID }: { csrfToken: string; administratorID: string }) {
  const { t } = useI18n();
  return (
    <div className="page-stack monitor-page billing-page">
      <ManagementHeader
        eyebrow="BILLING"
        title={t("费用与点数", "Billing & points")}
        description={t(
          "核查点数流水、结算依据和计价规则。",
          "Review points, settlement evidence, and pricing rules.",
        )}
      />
      <nav
        className="page-tabs"
        aria-label={t("费用分区", "Billing sections")}
      >
        <NavLink className={tabClass} to="/billing/points">
          {t("团队账户", "Team accounts")}
        </NavLink>
        <NavLink className={tabClass} to="/billing/records">
          {t("费用与流水", "Costs & ledger")}
        </NavLink>
        <NavLink className={tabClass} to="/billing/diagnostics">{t("采集诊断", "Collection diagnostics")}</NavLink>
        <NavLink className={tabClass} to="/billing/prices">{t("参考价格", "Reference prices")}</NavLink>
        <NavLink className={tabClass} to="/billing/nodes">{t("集群与节点成本", "Cluster & node costs")}</NavLink>
        <NavLink className={tabClass} to="/billing/sandbox">{t("沙箱实际消耗", "Sandbox resource usage")}</NavLink>
        <NavLink className={tabClass} to="/billing/usage">
          {t("网关用量", "Gateway usage")}
        </NavLink>
        <NavLink className={tabClass} to="/billing/controls">{t("余额与使用限制", "Balance and usage limits")}</NavLink>
        <NavLink className={tabClass} to="/billing/rules">
          {t("计价规则", "Pricing rules")}
        </NavLink>
      </nav>
      <Routes>
        <Route index element={<Navigate to="points" replace />} />
        <Route path="points" element={<TeamAccounts csrfToken={csrfToken} />} />
        <Route path="records" element={<Entries csrfToken={csrfToken} administratorID={administratorID} />} />
        <Route path="diagnostics" element={<BillingDiagnosticsPage />} />
        <Route path="prices" element={<ReferencePricesPage csrfToken={csrfToken} />} />
        <Route path="prices/:ref" element={<ReferencePricesPage csrfToken={csrfToken} />} />
        <Route path="nodes" element={<NodeCostsPage />} />
        <Route path="nodes/:ref" element={<NodeCostsPage />} />
        <Route path="sandbox" element={<SandboxUsagePage csrfToken={csrfToken} />} />
        <Route path="sandbox/:ref" element={<SandboxUsagePage csrfToken={csrfToken} />} />
        <Route path="usage" element={<LLMUsage />} />
        <Route path="rules" element={<Pricing csrfToken={csrfToken} />} />
        <Route path="controls" element={<BillingControlPage csrfToken={csrfToken} />} />
      </Routes>
    </div>
  );
}
type Summary = {
  balance_points: string;
  reserved_points: string;
  unsettled_points?: string;
  available_points: string;
};

const lifecycleNames: Record<IDSTeam["lifecycle"], [string, string]> = {
  active: ["正常", "Active"],
  provisioning: ["创建中", "Provisioning"],
  disabled: ["已停用", "Disabled"],
  tombstoned: ["已删除", "Deleted"],
};

const TEAM_PAGE_SIZE = 20;
const TEAM_SORT = "display_name" as const;

/** 团队点数账户：先按名称/ID 找到团队，再对它发放点数。
 *
 * 以前这里要求管理员手输 8 位团队 ID，等于把"记住 ID"的负担丢给人。
 * 现在列表直接给出名称与 ID，余额按当前页批量读取，发放走对话框并保留
 * 幂等命令 ID：失败时原样重试不会重复入账。 */
function TeamAccounts({ csrfToken }: { csrfToken: string }) {
  const { t } = useI18n();
  const [teams, setTeams] = useState<IDSTeam[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [lifecycle, setLifecycle] = useState<"" | IDSTeam["lifecycle"]>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [balances, setBalances] = useState<
    Record<string, Summary | "error" | undefined>
  >({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [target, setTarget] = useState<IDSTeam | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    listIDSTeams({
      page: Math.floor(offset / TEAM_PAGE_SIZE) + 1,
      page_size: TEAM_PAGE_SIZE,
      search: search || undefined,
      lifecycle: lifecycle || undefined,
      sort: TEAM_SORT,
      direction: "asc",
    })
      .then((page) => {
        if (!live) return;
        setTeams(page.items);
        setTotal(page.total);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setTeams([]);
        setError(cause);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [offset, search, lifecycle, reloadKey]);

  // 余额只能按团队逐个读取（账本没有批量接口）。限制在当前页并发，
  // 单条失败只让那一行显示"—"，不影响其它行，也不阻塞发放。
  useEffect(() => {
    if (!teams || teams.length === 0) return;
    let live = true;
    setBalancesLoading(true);
    void Promise.allSettled(
      teams.map((team) =>
        requestJSON<Summary>(
          "/api/v1/billing/summary?" + windowQuery(team.team_id),
        ),
      ),
    )
      .then((results) => {
        if (!live) return;
        const next: Record<string, Summary | "error"> = {};
        results.forEach((result, index) => {
          next[teams[index].team_id] =
            result.status === "fulfilled" ? result.value : "error";
        });
        setBalances(next);
      })
      .finally(() => {
        if (live) setBalancesLoading(false);
      });
    return () => {
      live = false;
    };
  }, [teams]);

  async function reloadBalance(teamID: string) {
    try {
      const value = await requestJSON<Summary>(
        "/api/v1/billing/summary?" + windowQuery(teamID),
      );
      setBalances((current) => ({ ...current, [teamID]: value }));
      return value;
    } catch {
      setBalances((current) => ({ ...current, [teamID]: "error" }));
      return null;
    }
  }

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setSearch(draft.trim());
  };

  return (
    <ManagementPanel
      title={t("团队点数账户", "Team points accounts")}
      eyebrow="TEAM POINTS"
      actions={
        <button
          className="secondary-button"
          type="button"
          disabled={loading}
          onClick={() => setReloadKey((value) => value + 1)}
        >
          {t("刷新", "Refresh")}
        </button>
      }
    >
      <form className="directory-toolbar" onSubmit={submitSearch}>
        <label className="directory-search">
          <span>{t("搜索团队", "Search teams")}</span>
          <input
            value={draft}
            placeholder={t("团队名称或 ID", "Team name or ID")}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <label>
          {t("生命周期", "Lifecycle")}
          <select
            value={lifecycle}
            onChange={(event) => {
              setOffset(0);
              setLifecycle(event.target.value as "" | IDSTeam["lifecycle"]);
            }}
          >
            <option value="">{t("全部", "All")}</option>
            {(Object.keys(lifecycleNames) as Array<IDSTeam["lifecycle"]>).map(
              (value) => (
                <option key={value} value={value}>
                  {t(lifecycleNames[value][0], lifecycleNames[value][1])}
                </option>
              ),
            )}
          </select>
        </label>
        <button className="secondary-button" disabled={loading}>
          {t("搜索", "Search")}
        </button>
      </form>
      <ManagementMessages error={error} />
      {loading && <ManagementLoading />}
      {!loading && teams && teams.length === 0 && !error ? (
        <ManagementEmpty>
          {t("没有匹配的团队。", "No teams match this search.")}
        </ManagementEmpty>
      ) : null}
      {!loading && teams && teams.length > 0 ? (
        <div className="administrator-table-wrap">
          <table className="administrator-table">
            <thead>
              <tr>
                <th>{t("团队", "Team")}</th>
                <th>{t("生命周期", "Lifecycle")}</th>
                <th>{t("数据中心", "Data center")}</th>
                <th>{t("点数", "Points")}</th>
                <th>{t("操作", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => {
                const balance = balances[team.team_id];
                return (
                  <tr key={team.team_id}>
                    <td>
                      <strong>{team.display_name || team.team_id}</strong>
                      <span>
                        {team.team_id}
                        {team.canonical_slug ? ` · ${team.canonical_slug}` : ""}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`user-state user-state-${
                          team.lifecycle === "active" ? "active" : "disabled"
                        }`}
                      >
                        {t(
                          lifecycleNames[team.lifecycle][0],
                          lifecycleNames[team.lifecycle][1],
                        )}
                      </span>
                    </td>
                    <td>{team.home_data_center_id || "—"}</td>
                    <td>
                      {balance === undefined ? (
                        <span>
                          {balancesLoading
                            ? t("读取中…", "Loading…")
                            : t("尚未读取", "Not loaded")}
                        </span>
                      ) : balance === "error" ? (
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => void reloadBalance(team.team_id)}
                        >
                          {t("重试", "Retry")}
                        </button>
                      ) : (
                        <>
                          <strong>
                            {t("可用", "Available")} {balance.available_points}
                          </strong>
                          <span>
                            {t("余额", "Balance")} {balance.balance_points} ·{" "}
                            {t("预留", "Reserved")} {balance.reserved_points} ·{" "}
                            {t("待结算", "Unsettled")} {balance.unsettled_points ?? "0"}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => setTarget(team)}
                        >
                          {t("发放点数", "Grant points")}
                        </button>
                        <Link to={`/billing/records?team_id=${encodeURIComponent(team.team_id)}`}>{t("查看流水", "View ledger")}</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {teams && teams.length > 0 ? (
        <ManagementPagination
          total={total}
          offset={offset}
          limit={TEAM_PAGE_SIZE}
          disabled={loading}
          onChange={(next) => setOffset(next)}
        />
      ) : null}
      <p className="monitor-note">
        {t(
          "点数发放不代表支付渠道已收款。自动扣点的实际开关与采集状态请查看「采集诊断」。",
          "A points grant is not proof of payment. See collection diagnostics for actual charging switches and collection status.",
        )}
      </p>
      {target ? (
        <GrantDialog
          team={target}
          csrfToken={csrfToken}
          balance={balances[target.team_id]}
          onClose={() => setTarget(null)}
          onGranted={() => void reloadBalance(target.team_id)}
        />
      ) : null}
    </ManagementPanel>
  );
}

/** 发放对话框：命令 ID 在打开时生成一次，失败后原样重试不会重复入账。 */
function GrantDialog({
  team,
  csrfToken,
  balance,
  onClose,
  onGranted,
}: {
  team: IDSTeam;
  csrfToken: string;
  balance: Summary | "error" | undefined;
  onClose: () => void;
  onGranted: () => void;
}) {
  const { t } = useI18n();
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState<{
    id: string;
    team_id: string;
    points: string;
    reason: string;
  } | null>(null);

  async function grant(event: React.FormEvent) {
    event.preventDefault();
    const command = pending ?? {
      id: createOperationId(),
      team_id: team.team_id,
      points,
      reason: reason.trim(),
    };
    setPending(command);
    setBusy(true);
    setError(null);
    try {
      const { reason: why, ...body } = command;
      await requestJSON("/api/v1/billing/grant", {
        method: "POST",
        csrfToken,
        reason: why,
        body,
      });
      setPending(null);
      setDone(true);
      setPoints("");
      onGranted();
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const current =
    balance && balance !== "error" ? balance.available_points : null;

  return (
    <ManagementDialog
      title={t("发放点数", "Grant points")}
      eyebrow="POINTS GRANT"
      onClose={onClose}
      disabled={busy}
    >
      <p className="panel-meta">
        <strong>{team.display_name || team.team_id}</strong>
        <span>
          {team.team_id}
          {current === null
            ? ` · ${t("当前余额未知", "Current balance unknown")}`
            : ` · ${t("可用", "Available")} ${current}`}
        </span>
      </p>
      <form className="monitor-form" onSubmit={grant}>
        <label>
          {t("发放点数", "Points to grant")}
          <input
            required
            inputMode="decimal"
            pattern="[0-9]+(\.[0-9]{1,6})?"
            value={points}
            disabled={busy}
            onChange={(event) => setPoints(event.target.value)}
          />
        </label>
        <label>
          {t("操作原因（必填，进入审计）", "Reason (required, audited)")}
          <input
            required
            maxLength={512}
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <ManagementMessages error={error} />
        {done ? (
          <div className="success-message" role="status">
            {t("已记录本次发放。", "This grant has been recorded.")}
          </div>
        ) : null}
        <div className="dialog-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            {t("关闭", "Close")}
          </button>
          <button className="primary-button" disabled={busy}>
            {pending
              ? t("重试本次入账", "Retry this grant")
              : t("确认发放", "Confirm grant")}
          </button>
        </div>
      </form>
      <p className="monitor-note">
        {t(
          "失败时保留当前内容重试即可，相同操作不会重复入账。",
          "If it fails, retry unchanged — the same operation will not credit twice.",
        )}
      </p>
    </ManagementDialog>
  );
}
const settlementReasons: Record<string, [string, string]> = {
 AWAITING_SETTLEMENT: ["等待结算处理；不代表余额不足", "Awaiting settlement; does not establish insufficient balance"],
 TEAM_UNATTRIBUTED: ["费用尚未关联团队", "Cost has no team attribution"],
 SOURCE_REVISION_REQUIRES_REVIEW: ["来源金额修订，需核对后调整", "Source amount revised; review before adjustment"],
 SOURCE_RETIRED: ["原计量来源已停用", "Metering source retired"],
 HISTORICAL_RULES_MISSING: ["缺少历史计价规则", "Historical pricing rules missing"],
 CATEGORY_DISABLED: ["该分类未启用计价", "Category pricing disabled"],
 COST_AMOUNT_UNKNOWN: ["来源费用金额未知", "Source cost amount unknown"],
 COST_BASIS_NOT_PERMITTED: ["该费用依据不在允许范围", "Cost basis not permitted"],
 USAGE_QUANTITY_UNKNOWN: ["计量数量未知", "Usage quantity unknown"],
 USAGE_UNIT_MISMATCH: ["用量单位与规则不一致", "Usage unit does not match pricing rules"],
 RESOURCE_UNIT_PRICE_MISSING: ["缺少对应资源单价", "Resource unit price missing"],
 PRICING_EVIDENCE_INVALID: ["计价依据不完整或无效", "Pricing evidence incomplete or invalid"],
};
const ENTRY_WINDOWS = [
  { days: 7, label: ["最近 7 天", "Last 7 days"] },
  { days: 30, label: ["最近 30 天", "Last 30 days"] },
  { days: 90, label: ["最近 90 天", "Last 90 days"] },
] as const;

/** 费用与流水：按时间窗与团队筛选账本，游标翻页。
 *
 * 只做展示与筛选——金额口径不变：未知金额保持未知，不补零；
 * 同一来源的修订版本不重复相加。 */
function ResourceCollectionStatus({refreshKey}: {refreshKey: string}) {
  const {t} = useI18n();
  type Status = {source: string; status: string; last_success_ms: number | null; result: {unavailable?: number; gaps?: number; charged?: number}};
  const [rows, setRows] = useState<Status[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setFailed(false);
    requestJSON<{items: Status[]}>("/api/v1/billing/collection").then((data) => {if(live) setRows(data.items);}).catch(() => {if(live) setFailed(true);});
    return () => {live = false;};
  }, [refreshKey]);
  const labels: Record<string, [string, string]> = {sandbox_compute: ["沙箱", "Sandboxes"], workspace_storage: ["工作区存储", "Workspace storage"], development_database: ["开发数据库", "Development databases"], publish_compute: ["动态发布", "Published compute"], published_database: ["发布数据库", "Published databases"]};
  return <section aria-label={t("资源计量状态", "Resource metering status")}>
    <p className="monitor-note">{t("资源按相邻采样估计用量，通常每 60 秒采集；超过 3 分钟的缺口不补扣。供应商账单金额另行核对。", "Resource usage is estimated between samples, normally every 60 seconds. Gaps over 3 minutes are not backcharged. Supplier invoice amounts require separate reconciliation.")}</p>
    {failed ? <p role="status">{t("计量状态读取失败", "Unable to read metering status")}</p> : rows?.length === 0 ? <p>{t("尚无资源采集状态，请检查来源连接配置。", "No resource collection status yet. Check source connection configuration.")}</p> : null}
    <dl className="monitor-summary">{rows?.map((row) => <div key={row.source}>
      <dt>{labels[row.source] ? t(...labels[row.source]) : row.source}</dt>
      <dd>{row.status === "retired" ? t("已改用沙箱实际消耗观测", "Replaced by actual sandbox usage") : row.status === "failed" ? t("采集失败", "Collection failed") : row.status === "running" ? t("采集中", "Collecting") : !row.last_success_ms || Date.now() - row.last_success_ms > 180000 ? t("采集延迟", "Collection delayed") : t("已采集", "Collected")}</dd>
      <small className="billing-source-detail">{t("最近成功", "Last success")}: {row.last_success_ms ? new Date(row.last_success_ms).toLocaleString(t("zh-CN", "en-US")) : "—"}</small>
      <small className="billing-source-detail">{t("未知资源 / 缺口", "Unknown resources / gaps")}: {row.result.unavailable ?? t("未知", "Unknown")} / {row.result.gaps ?? t("未知", "Unknown")}</small>
    </div>)}</dl>
  </section>;
}

function Entries({ csrfToken, administratorID }: { csrfToken: string; administratorID: string }) {
  const { t } = useI18n();
  const [urlQuery, setURLQuery] = useSearchParams();
  const [refresh, setRefresh] = useState(0);
  const [previousPages, setPreviousPages] = useState<string[]>([]);
  const [rows, setRows] = useState<Entry[] | null>(null);
  const [sourceMapping,setSourceMapping]=useState<{status:string;window_id?:string}|null>(null);
  const [error, setError] = useState<unknown>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [days, setDays] = useState<number>(30);
  const [teamDraft, setTeamDraft] = useState("");
  const [team, setTeam] = useState("");
  const [query, setQuery] = useState(() => windowQuery("", 30));
  const [filters, setFilters] = useState({ action: "", category: "", source: "", source_id: "", settlement_status: "", entry_id: "", reference_id: "" });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [refund, setRefund] = useState<(Entry & { team_id: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(urlQuery);
    if (!params.has("start_ms") || !params.has("end_ms")) {
      const window = new URLSearchParams(windowQuery(params.get("team_id") ?? "", 30));
      params.set("start_ms", window.get("start_ms")!); params.set("end_ms", window.get("end_ms")!);
    }
    setQuery(params.toString());
    if (!params.has("before")) setPreviousPages([]);
    setTeam(params.get("team_id") ?? ""); setTeamDraft(params.get("team_id") ?? "");
    const loaded = {action: params.get("action") ?? "", category: params.get("category") ?? "", source: params.get("source") ?? "", source_id: params.get("source_id") ?? "", settlement_status: params.get("settlement_status") ?? "", entry_id: params.get("entry_id") ?? "", reference_id: params.get("reference_id") ?? ""};
    setFilters(loaded); setAppliedFilters(loaded);
    const interval = (Number(params.get("end_ms"))-Number(params.get("start_ms"))) / 86400000;
    if (ENTRY_WINDOWS.some(item => item.days === interval)) setDays(interval);
  }, [urlQuery]);
  useEffect(() => {
    let live = true;
    setBusy(true);
    setError(null);
    setRows(null);
    setSourceMapping(null);
    setCursor(null);
    requestJSON<{ items: Entry[]; next_cursor: string | null; source_mapping?: {status:string;window_id?:string}|null }>(
      "/api/v1/billing/entries?" + query,
    )
      .then((d) => {
        if (live) {
          setRows(d.items); setSourceMapping(d.source_mapping ?? null);
          setCursor(d.next_cursor);
        }
      })
      .catch((cause: unknown) => {
        if (live) setError(cause);
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [query, refresh]);
  const applyWindow = (nextDays: number, nextTeam: string, nextFilters = appliedFilters) => {
    setDays(nextDays);
    setTeam(nextTeam);
    setAppliedFilters(nextFilters);
    const params = new URLSearchParams(windowQuery(nextTeam, nextDays));
    for (const [key, value] of Object.entries(nextFilters)) if (value.trim()) params.set(key, value.trim());
    setPreviousPages([]);
    setURLQuery(params);
    setRefresh(value => value + 1);
  };
  return (
    <ManagementPanel
      title={t("账本记录", "Ledger records")}
      eyebrow="LEDGER"
      actions={
        <button
          className="secondary-button"
          type="button"
          disabled={busy}
          onClick={() => applyWindow(days, team)}
        >
          {t("刷新", "Refresh")}
        </button>
      }
    >
      <ResourceCollectionStatus refreshKey={query} />
      <form
        className="directory-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          applyWindow(days, teamDraft.trim(), filters);
        }}
      >
        <label>
          {t("时间窗", "Window")}
          <select
            value={days}
            onChange={(event) =>
              applyWindow(Number(event.target.value), team)
            }
          >
            {ENTRY_WINDOWS.map((item) => (
              <option key={item.days} value={item.days}>
                {t(item.label[0], item.label[1])}
              </option>
            ))}
          </select>
        </label>
        <label className="directory-search">
          <span>{t("团队 ID", "Team ID")}</span>
          <input
            value={teamDraft}
            placeholder={t("留空查看全部团队", "Blank shows every team")}
            pattern="[0-9abcdefghjkmnpqrstvwxyz]{8}"
            onChange={(event) => setTeamDraft(event.target.value)}
          />
        </label>
        <label>{t("操作类型", "Action")}<select value={filters.action} onChange={event => setFilters({ ...filters, action: event.target.value })}>
          <option value="">{t("全部", "All")}</option>
          {[["cost", t("费用记录", "Cost")], ["capture", t("消费", "Charge")], ["grant", t("发放", "Grant")], ["refund", t("退款", "Refund")], ["reserve", t("预留", "Reserve")], ["extend", t("追加预留", "Extend")], ["release", t("释放", "Release")]].map(([value,label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>{t("消耗分类", "Category")}<select value={filters.category} onChange={event => setFilters({ ...filters, category: event.target.value })}>
          <option value="">{t("全部", "All")}</option>{categories.map(value => <option key={value} value={value}>{t(names[value][0],names[value][1])}</option>)}
        </select></label>
        <label>{t("结算状态", "Settlement status")}<select value={filters.settlement_status} onChange={event => setFilters({...filters, settlement_status:event.target.value})}>
          <option value="">{t("全部", "All")}</option>
          {[["review","来源修订待核对","Source revision review"],["pending","待结算","Pending"],["unpriced","无法计价","Unpriced"],["unattributed","未归属团队","Unattributed"],["legacy_review","历史待核对","Legacy review"],["source_retired","来源已停用","Retired source"],["settled","已结算","Settled"]].map(([value,zh,en])=><option key={value} value={value}>{t(zh,en)}</option>)}
        </select></label>
        <label>{t("流水 ID", "Entry ID")}<input value={filters.entry_id} pattern="[a-f0-9]{64}" onChange={event => setFilters({...filters,entry_id:event.target.value})}/></label>
        <label>{t("关联原流水 ID", "Original entry ID")}<input value={filters.reference_id} pattern="[a-f0-9]{64}" onChange={event => setFilters({...filters,reference_id:event.target.value})}/></label>
        <label>{t("计量来源", "Metering source")}<input value={filters.source} pattern="[A-Za-z0-9_.:-]{1,128}" onChange={event => setFilters({ ...filters, source: event.target.value })} required={!!filters.source_id} /></label>
        <label>{t("来源记录 ID", "Source record ID")}<input value={filters.source_id} pattern="[A-Za-z0-9_.:-]{1,128}" onChange={event => setFilters({ ...filters, source_id: event.target.value })} /></label>
        <button className="secondary-button" disabled={busy}>
          {t("应用", "Apply")}
        </button>
      </form>
      <p className="monitor-note">
        {t(
          "仅展示已记录的结算依据；网关报告费用不是最终供应商账单。未知金额不会补成零；同一来源的修订版本不能重复相加。",
          "Recorded settlement evidence only. Gateway costs are not final supplier invoices. Unknown amounts remain unknown; revisions must not be summed.",
        )}
      </p>
      {filters.entry_id || filters.reference_id ? <p role="status">{t("按流水 ID 精确查询，不受时间窗限制。", "Exact ledger ID lookup is independent of the time window.")} <Link to="/billing/records">{t("清除筛选", "Clear filters")}</Link></p> : null}
      {sourceMapping&&<p role="status">{sourceMapping.status==="hourly_window"?t("该模型请求归集到小时费用窗口；窗口未关闭时尚无正式扣款流水。请求明细保留在网关。", "This request belongs to an hourly cost window. Open windows have no finalized debit yet; request details remain in the gateway."):t("未找到轻量关联记录，可能尚未采集或已过保留期；不代表没有发生费用。", "No lightweight receipt found: it may not be collected or may have expired. This does not establish zero cost.")}</p>}
      <ManagementMessages error={error} />
      {busy && !rows ? <ManagementLoading /> : null}
      {rows && rows.length === 0 ? (
        <ManagementEmpty>{t("暂无账本记录", "No ledger records")}</ManagementEmpty>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="administrator-table-wrap">
          <table className="administrator-table">
            <thead>
              <tr>
                {[
                  t("时间", "Time"),
                  t("团队", "Team"),
                  t("操作", "Action"),
                  t("分类", "Category"),
                  t("余额变动（点）", "Balance change (points)"),
                  t("来源费用（USD）", "Reported cost (USD)"),
                  t("费用结算", "Cost settlement"),
                  t("计量依据", "Metering evidence"),
                  t("来源记录", "Source record"),
                ].map((x) => (
                  <th key={x} scope="col">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const evidence = r.snapshot.evidence;
                const basis = evidence ? basisNames[evidence.cost_basis] : undefined;
                const usageEntries = Object.entries(evidence?.usage_details ?? {});
                return (
                  <tr key={r.id}>
                    <td>
                      <span>
                        {new Date(r.created_ms).toLocaleString(
                          t("zh-CN", "en-US"),
                        )}
                      </span>
                    </td>
                    <td>
                      {r.team_id ? <Link to={`/team-runtime?team_id=${encodeURIComponent(r.team_id)}`}>{r.team_id}</Link> : t("未归属", "Unattributed")}
                    </td>
                    <td>
                      {(
                        {
                          grant: t("发放", "Grant"),
                          reserve: t("预留", "Reserve"),
                          extend: t("追加预留", "Extend"),
                          capture: t("消费", "Consume"),
                          release: t("释放", "Release"),
                          refund: t("退款", "Refund"),
                          cost: t("费用快照", "Cost snapshot"),
                        } as Record<string, string>
                      )[r.action] ?? r.action}
                    </td>
                    <td>
                      {r.category && r.category in names
                        ? t(
                            names[r.category as keyof typeof names][0],
                            names[r.category as keyof typeof names][1],
                          )
                        : "—"}
                    </td>
                    <td>
                      <strong>{r.balance_delta}</strong>
                    </td>
                    <td>
                      <strong>
                        {evidence
                          ? (evidence.reported_usd ?? t("未知", "Unknown"))
                          : "—"}
                      </strong>
                      {evidence ? (
                        <span>
                          {basis ? t(basis[0], basis[1]) : evidence.cost_basis}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {r.settlement_status
                        ? ({
                            review: t("来源修订待核对", "Source revision needs review"),
                            source_retired: t("旧口径已停止自动结算", "Old source no longer auto-settles"),
                            settled: t("已结算", "Settled"),
                            pending: t("待结算", "Pending settlement"),
                            unattributed: t("未归属团队", "Unattributed"),
                            legacy_review: t("历史费用待核实", "Legacy cost needs review"),
                            unpriced: t("缺少有效计价依据", "No valid pricing basis"),
                          })[r.settlement_status]
                        : "—"}
                      {r.settlement_reason ? <small>{t(...(settlementReasons[r.settlement_reason] ?? [r.settlement_reason, r.settlement_reason]))}</small> : null}
                    </td>
                    <td>
                      {evidence?.resource_ref ? <small>{t("资源", "Resource")}: {evidence.resource_ref}</small> : null}
                      {evidence?.measurement_basis ? <small>{({sampled_allocation: t("分配资源采样估计", "Sampled allocation estimate"), sampled_storage: t("容量采样估计", "Sampled storage estimate"), unmeasured_gap: t("计量缺口，不补扣", "Measurement gap; no backcharge")} as Record<string, string>)[evidence.measurement_basis] ?? evidence.measurement_basis}</small> : null}
                      {evidence?.quantity != null && evidence?.measurement_basis ? <small>{evidence.quantity} {evidence.unit}</small> : null}
                      {usageEntries.length > 0 ? (
                        usageEntries.map(([key, value]) => (
                          <small key={key}>
                            {usageNames[key]
                              ? t(usageNames[key][0], usageNames[key][1])
                              : key}
                            {": "}
                            {typeof value === "number"
                              ? value.toLocaleString()
                              : "—"}
                          </small>
                        ))
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td>
                      <small>{evidence?.source ?? "—"}</small>
                      <small>{evidence?.source_id ?? "—"}</small>
                      <details><summary>{t("流水 ID", "Ledger entry ID")}</summary><code>{r.id}</code></details>
                      {r.action === "capture" ? <div>
                        <small>{t("累计已退 / 剩余可退", "Refunded / refundable")}: {r.refunded_points ?? t("未知", "Unknown")} / {r.refundable_points ?? t("未知", "Unknown")}</small>
                        <Link to={`/billing/records?reference_id=${r.id}`}>{t("查看关联退款", "View refunds")}</Link>
                      </div> : null}
                      {r.action === "refund" && r.reference_id ? <Link to={`/billing/records?entry_id=${r.reference_id}`}>{t("查看原扣点", "View original charge")}</Link> : null}
                      {r.team_id && r.category === "sandbox" ? <Link to={`/billing/sandbox?team_id=${encodeURIComponent(r.team_id!)}`}>{t("团队沙箱观测", "Team sandbox observations")}</Link> : null}
                      {r.action === "capture" && r.team_id ? <button className="secondary-button" type="button" onClick={() => setRefund({ ...r, team_id: r.team_id! })}>{t("退还点数", "Refund points")}</button> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {refund ? <RefundDialog administratorID={administratorID} charge={refund} csrfToken={csrfToken} onClose={() => setRefund(null)} onRefunded={() => setRefresh(value => value + 1)} /> : null}
      {cursor || previousPages.length ? (
        <div className="directory-pagination">
          <span>
            {t("按流水时间倒序排列", "Ordered by ledger time, newest first")}
          </span>
          <div>
            <button className="secondary-button" disabled={busy || previousPages.length === 0} onClick={() => {const previous = previousPages[previousPages.length-1];setPreviousPages(pages=>pages.slice(0,-1));setURLQuery(previous);}}>{t("上一页", "Previous page")}</button>
            <button
              className="secondary-button"
              type="button"
              disabled={busy || !cursor}
              onClick={() => {
                const q = new URLSearchParams(query);
                if (!cursor) return;
                q.set("before", cursor);
                setPreviousPages(pages=>[...pages,query]);
                setURLQuery(q);
              }}
            >
              {t("下一页", "Next page")}
            </button>
          </div>
        </div>
      ) : null}
    </ManagementPanel>
  );
}
function Pricing({ csrfToken }: { csrfToken: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<Rules | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() {
    setBusy(true);
    setMessage("");
    try {
      setData(await requestJSON<Rules>("/api/v1/billing/rules"));
    } catch {
      setData(null);
      setMessage(t("规则读取失败", "Unable to load rules"));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  function update(category: string, key: keyof Rule, value: string | string[] | Record<string, string>) {
    if (data)
      setData({
        ...data,
        categories: {
          ...data.categories,
          [category]: { ...data.categories[category], [key]: value },
        },
      });
  }
  // A cost multiplier has nothing to multiply without a permitted evidence
  // basis, and the ledger refuses such a rule outright. Catching it here keeps
  // the operator from losing the whole edit to a rejected round-trip.
  const unbased = data
    ? categories.filter(
        (c) =>
          data.categories[c].mode === "cost_factor" &&
          data.categories[c].permitted_basis.length === 0,
      )
    : [];
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!data) return;
    if (unbased.length > 0) {
      const list = unbased
        .map((c) => t(names[c][0], names[c][1]))
        .join(t("、", ", "));
      setMessage(
        t(
          `费用乘系数必须至少勾选一个「允许的计价依据」，否则结算会被拒绝：${list}`,
          `A cost multiplier needs at least one permitted cost basis, or settlement is refused: ${list}`,
        ),
      );
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const { revision, ...config } = data;
      setData(
        await requestJSON<Rules>("/api/v1/billing/rules", {
          method: "PUT",
          csrfToken,
          reason,
          body: { expected_revision: revision, config },
        }),
      );
      setMessage(
        t(
          "规则已保存，已有预留继续使用原规则。",
          "Saved. Existing reservations retain their original rules.",
        ),
      );
    } catch (error) {
      // The component explains exactly which rule rejected the edit; hiding it
      // behind a generic message is what made a rejected save look like a bug.
      const detail = error instanceof Error ? error.message.trim() : "";
      setMessage(
        detail
          ? t(`保存被拒绝：${detail}`, `Save rejected: ${detail}`)
          : t(
              "保存未确认。请重新读取版本后核对。",
              "Save unconfirmed. Reload the revision and review.",
            ),
      );
    } finally {
      setBusy(false);
    }
  }
  const enabledCount = data
    ? categories.filter((c) => c !== "sandbox" && data.categories[c].mode !== "disabled").length
    : 0;
  return (
    <ManagementPanel
      title={t("计价规则", "Pricing rules")}
      actions={
        <button
          className="secondary-button"
          disabled={busy}
          onClick={() => void load()}
        >
          {t("重新读取", "Reload")}
        </button>
      }
    >
      {message && (
        <p className="monitor-note" role="status">
          {message}
        </p>
      )}
      {data && (
        <form className="billing-rules" onSubmit={save}>
          <dl className="monitor-summary billing-rules-summary">
            <div>
              <dt>{t("规则版本", "Rules revision")}</dt>
              <dd>{data.revision}</dd>
              <small>
                {t(
                  "已有预留继续使用原规则",
                  "Existing reservations keep their rules",
                )}
              </small>
            </div>
            <div>
              <dt>{t("已启用计价类别", "Categories priced")}</dt>
              <dd>
                {enabledCount}
                <span className="billing-of">/{categories.length}</span>
              </dd>
              <small>
                {t(
                  "未启用的类别只记费用，不扣点",
                  "Disabled categories record cost without charging",
                )}
              </small>
            </div>
            <div>
              <dt>{t("每美元点数", "Points per USD")}</dt>
              <dd>{data.points_per_usd}</dd>
              <small>
                {t("所有类别共用同一换算", "Shared by every category")}
              </small>
            </div>
          </dl>

          <section className="billing-block">
            <h3 className="billing-block-title">
              {t("点数换算", "Points conversion")}
            </h3>
            <div className="billing-block-body">
              <label className="billing-field">
                <span>{t("每美元点数", "Points per USD")}</span>
                <input
                  required
                  inputMode="decimal"
                  value={data.points_per_usd}
                  disabled={busy}
                  onChange={(e) =>
                    setData({ ...data, points_per_usd: e.target.value })
                  }
                />
              </label>
              <p className="billing-hint">
                {t(
                  "只影响新的预留；已有预留沿用冻结时的规则版本。",
                  "Only new reservations use this rate; existing ones keep the rules frozen at their creation.",
                )}
              </p>
            </div>
          </section>

          <section className="billing-block">
            <h3 className="billing-block-title">
              {t("分类计价", "Category pricing")}
            </h3>
            <div className="billing-cards">
              {categories.map((c) => {
                if (c === "sandbox") return <article className="billing-card" key={c}><h4>{t("沙箱", "Sandboxes")}</h4><p>{t("本项先记录实际资源消耗与 AWS 成本证据，已停止原 requests 采样扣点。请到“沙箱实际消耗”查看用量；这里不配置沙箱收费算法。", "This item records actual resource usage and AWS cost evidence. The previous requests-based debit collector is stopped. View Sandbox resource usage; sandbox charging algorithms are not configured here.")}</p><NavLink to="/billing/sandbox">{t("查看实际消耗", "View actual usage")}</NavLink></article>;
                const rule = data.categories[c];
                const mode = modeInfo[rule.mode] ?? modeInfo.disabled;
                return (
                  <article className="billing-card" key={c}>
                    <header className="billing-card-head">
                      <div className="billing-card-title">
                        <h4>{t(names[c][0], names[c][1])}</h4>
                        <span className={`billing-mode ${mode.tone}`}>
                          {t(mode.label[0], mode.label[1])}
                        </span>
                      </div>
                      <label className="billing-field">
                        <span>{t("计价方式", "Pricing mode")}</span>
                        <select
                          aria-label={t("计价方式", "Pricing mode")}
                          value={rule.mode}
                          disabled={busy}
                          onChange={(e) => update(c, "mode", e.target.value)}
                        >
                          <option value="disabled">
                            {t("未启用", "Disabled")}
                          </option>
                          <option value="free">{t("免费", "Free")}</option>
                          <option value="cost_factor">
                            {t("费用乘系数", "Cost multiplier")}
                          </option>
                          {c !== "token" && <option value="resource_price">{t("资源分项单价", "Resource unit prices")}</option>}
                          <option value="unit_price">
                            {t("用量乘单价", "Unit price")}
                          </option>
                        </select>
                      </label>
                    </header>
                    <p className="billing-hint">{t(mode.hint[0], mode.hint[1])}</p>
                    {rule.mode === "cost_factor" && (
                      <div className="billing-card-body">
                        <label className="billing-field">
                          <span>{t("系数", "Multiplier")}</span>
                          <input
                            required
                            value={rule.factor}
                            disabled={busy}
                            onChange={(e) => update(c, "factor", e.target.value)}
                          />
                        </label>
                        <fieldset className="billing-basis" disabled={busy}>
                          <legend>
                            {t("允许的计价依据", "Permitted cost basis")}
                          </legend>
                          <div className="billing-chips">
                            {basisOptions.map(([basis, label]) => (
                              <label className="billing-chip" key={basis}>
                                <input
                                  type="checkbox"
                                  checked={rule.permitted_basis.includes(basis)}
                                  onChange={(e) =>
                                    update(
                                      c,
                                      "permitted_basis",
                                      e.target.checked
                                        ? [...rule.permitted_basis, basis]
                                        : rule.permitted_basis.filter(
                                            (x) => x !== basis,
                                          ),
                                    )
                                  }
                                />
                                <span>{t(label[0], label[1])}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        {rule.permitted_basis.length === 0 ? (
                          <p className="billing-warning" role="alert">
                            {t(
                              "至少要勾选一个依据，否则这条规则无法保存，结算也会被拒绝。",
                              "Select at least one basis, or this rule cannot be saved and settlement will be refused.",
                            )}
                          </p>
                        ) : (
                          <p className="billing-hint">
                            {t(
                              "没有可用的合法依据时拒绝结算，不会当作免费。",
                              "Without a permitted basis the ledger refuses to settle rather than treating it as free.",
                            )}
                          </p>
                        )}
                      </div>
                    )}
                    {rule.mode === "resource_price" && (
                      <div className="billing-card-body">
                        {(c === "publish" ? ["vcpu_hour", "gib_hour"] : ["gib_hour"]).map((unit) => (
                          <label className="billing-field" key={unit}>
                            <span>{unit === "vcpu_hour" ? t("每 vCPU 小时美元单价", "USD per vCPU hour") : c === "publish" ? t("每 GiB 内存小时美元单价", "USD per GiB memory hour") : t("每 GiB 容量小时美元单价", "USD per GiB storage hour")}</span>
                            <input required inputMode="decimal" disabled={busy} value={rule.unit_prices_usd?.[unit] ?? ""}
                              onChange={(e) => update(c, "unit_prices_usd", {...rule.unit_prices_usd, [unit]: e.target.value})} />
                          </label>
                        ))}
                      </div>
                    )}
                    {rule.mode === "unit_price" && (
                      <div className="billing-card-body">
                        <label className="billing-field">
                          <span>{t("计量单位", "Usage unit")}</span>
                          <input
                            required
                            value={rule.unit}
                            disabled={busy}
                            onChange={(e) => update(c, "unit", e.target.value)}
                          />
                        </label>
                        <label className="billing-field">
                          <span>{t("每单位美元单价", "USD per unit")}</span>
                          <input
                            required
                            value={rule.unit_price_usd}
                            disabled={busy}
                            onChange={(e) =>
                              update(c, "unit_price_usd", e.target.value)
                            }
                          />
                        </label>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>

          <section className="billing-block">
            <h3 className="billing-block-title">
              {t("余额不足策略", "Insufficient balance policy")}
            </h3>
            <div className="billing-cards">
              <p><Link to="/billing/controls">{t("模型、沙箱和动态发布策略在「余额与使用限制」配置", "Configure model, sandbox and dynamic publish policies in Balance and usage limits")}</Link></p>
              <article className="billing-card">
                <header className="billing-card-head">
                  <div className="billing-card-title">
                    <h4>
                      {t("存储欠费与保留规则", "Storage debt and retention")}
                    </h4>
                  </div>
                </header>
                <div className="billing-card-body">
                  <label className="billing-field">
                    <span>
                      {t(
                        "存储余额不足处理",
                        "Storage action on insufficient balance",
                      )}
                    </span>
                    <select
                      value={data.storage_insufficient_balance.mode}
                      disabled={busy}
                      onChange={(e) =>
                        setData({
                          ...data,
                          storage_insufficient_balance: {
                            ...data.storage_insufficient_balance,
                            mode: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="unconfigured">
                        {t("尚未配置", "Not configured")}
                      </option>
                      <option value="alert_only">
                        {t(
                          "仅告警，记录未结算消耗",
                          "Alert and record unsettled usage",
                        )}
                      </option>
                      <option value="block_new_writes">
                        {t(
                          "禁止新增写入，保留已有数据",
                          "Block new writes and retain existing data",
                        )}
                      </option>
                    </select>
                  </label>
                  <label className="billing-field">
                    <span>
                      {t(
                        "最大欠费点数（留空为未配置）",
                        "Maximum debt in points (empty means unset)",
                      )}
                    </span>
                    <input
                      inputMode="decimal"
                      pattern="[0-9]+([.][0-9]{1,6})?"
                      disabled={busy}
                      value={
                        data.storage_insufficient_balance.max_debt_points ?? ""
                      }
                      onChange={(e) =>
                        setData({
                          ...data,
                          storage_insufficient_balance: {
                            ...data.storage_insufficient_balance,
                            max_debt_points: e.target.value || null,
                          },
                        })
                      }
                    />
                  </label>
                  <label className="billing-field">
                    <span>{t("数据保留方式", "Data retention policy")}</span>
                    <select
                      value={data.storage_insufficient_balance.retention_mode}
                      disabled={busy}
                      onChange={(e) =>
                        setData({
                          ...data,
                          storage_insufficient_balance: {
                            ...data.storage_insufficient_balance,
                            retention_mode: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="retain">
                        {t("持续保留数据", "Retain data")}
                      </option>
                      <option value="review_after_days">
                        {t(
                          "保留指定天数后人工处理",
                          "Review manually after a retention period",
                        )}
                      </option>
                    </select>
                  </label>
                  <label className="billing-field">
                    <span>{t("保留天数", "Retention days")}</span>
                    <input
                      type="number"
                      min={1}
                      max={3650}
                      required={
                        data.storage_insufficient_balance.retention_mode ===
                        "review_after_days"
                      }
                      disabled={
                        busy ||
                        data.storage_insufficient_balance.retention_mode !==
                          "review_after_days"
                      }
                      value={
                        data.storage_insufficient_balance.retention_days ?? ""
                      }
                      onChange={(e) =>
                        setData({
                          ...data,
                          storage_insufficient_balance: {
                            ...data.storage_insufficient_balance,
                            retention_days:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          },
                        })
                      }
                    />
                  </label>
                  <p className="billing-hint">
                    {t(
                      "保留期间仍可能产生存储费用。0 点表示不允许额外欠费；留空不表示无限额度。当前不会自动清理数据。",
                      "Storage may continue to incur costs. Zero permits no additional debt; empty does not mean unlimited credit. Data is not deleted automatically.",
                    )}
                  </p>
                </div>
              </article>
            </div>
          </section>

          <footer className="billing-save">
            <label className="billing-field">
              <span>{t("修改原因", "Reason for change")}</span>
              <input
                required
                value={reason}
                disabled={busy}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <button className="primary-button" disabled={busy}>
              {t("保存规则", "Save rules")}
            </button>
          </footer>
        </form>
      )}
    </ManagementPanel>
  );
}
